// The help centre's content (/help). The articles live in the help store (photohawk-serverless api-support-kb,
// table support-help-<stage>) and are edited by the owner and support in /platform/help-centre. The SSR server reads
// the PUBLISHED ones from the public API at request time (./remote.ts: cached a few minutes, the last good copy kept
// when the API is down) and applies them here with applyHelpSnapshot(); the page embeds what it used
// (window.__PH_HELP__) so the browser hydrates from the same content, and other languages load from the API.
//
// The Markdown files in src/content/help/articles/<slug>/<lang>.md are the one-time SEED (scripts/help/seed-export.ts →
// .github/workflows/help-seed-import.yml) and, on the server only, the content of last resort when the API has never
// answered. Same body format as the guides (src/content/guides/body.ts: Markdown + :::callout / :::figure / :::video /
// :::link), so the guides' renderer draws both. An article's fields (in the store, or the seed's front matter):
//
//   slug: buying-and-downloading-photos          the URL (/help/articles/<slug>)
//   title: / description:                        H1 and meta description (≤ 155 characters)
//   section: photographers | photos | app        "For photographers & organisers" (Intercom's Member guide),
//                                                "Find & download your photos" (Intercom's Customer support), or
//                                                "Photohawk app" (the iPhone app; a section apart: SEPARATE_SECTIONS)
//   category: <id from the catalogue>            must belong to that section (seed: categories.json)
//   updated: YYYY-MM-DD
//   keywords: / related:                         lists (search terms; related article slugs)
//   intercomId: / intercomUrl:                   seed files only: where it came from (the redirect map)
//   appPromo: auto | off                         photos articles only: "auto" may show the Photohawk app card,
//                                                never in a Pro/Max workspace's context (./promo.ts). Default auto.
//   faq:                                         optional q/a pairs (FAQPage JSON-LD; shown on the page)
//   translation: machine | reviewed              non-English only (required there): "machine" until a native
//                                                speaker has reviewed it. A machine translation is served (?lang=xx)
//                                                but kept out of search: noindex, no hreflang, not in the sitemap.
//
// Plan names and prices use the guides' tokens, never typed figures: {{newPlan.<free|pro|max>.<field>}} for the plans
// from 1 Nov 2026 ({{plan.<id>.<field>}} for today's).

import { SEO_LANGS, type SeoLang } from "@/lib/seo/site";
import { HELP_PATH } from "@/lib/helpFlag";
import { blocksText, countWords, parseGuideBody, type GuideBlock } from "@/content/guides/body";
import { splitFrontMatter, type FrontMatter } from "@/content/guides/frontMatter";
import { fillPlanTokens, type GuideFaq } from "@/content/guides";
import SEED_CATALOGUE from "./categories.json";
import { normalise } from "./search";
import { isSeparateSection, sameHelpWall, type HelpSectionId } from "./sections";

export { HELP_PATH };

/**
 * The languages the help centre is written in: every article, the category titles and the help UI strings (help.* in
 * src/i18n). The site UI's languages plus French, which so far is the help centre's only (src/i18n/helpLocales/fr.json:
 * on a French help page the site's nav and footer stay in English). The help store accepts the same list
 * (photohawk-serverless api-support-kb src/help/model.js LANGS), and so does its editor (HELP_EDIT_LANGS).
 */
export const HELP_CONTENT_LANGS = ["en", "es", "pt", "de", "fr", "it", "ja", "zh", "ms"] as const satisfies readonly SeoLang[];

export { HELP_SECTION_IDS, SEPARATE_SECTIONS, isSeparateSection, sameHelpWall, type HelpSectionId } from "./sections";

export interface HelpSection {
  id: HelpSectionId;
  /** The Intercom help centre it replaces. */
  intercomHelpCenter: string;
  /** member = photographers / organisers (Studio users); customer = people finding and buying photos; app = people
   *  using the Photohawk app. */
  audience: "member" | "customer" | "app";
  intercomNames?: string[];
}

export interface HelpCategory {
  id: string;
  section: HelpSectionId;
  icon: string;
  order: number;
  /** Per language; English is required. */
  title: Partial<Record<SeoLang, string>> & { en: string };
  description: Partial<Record<SeoLang, string>> & { en: string };
  /** Intercom collection ids this category replaces (the redirect map). */
  intercomCollections: string[];
}

export interface HelpCatalogue {
  sections: HelpSection[];
  categories: HelpCategory[];
}

export type HelpTranslation = "machine" | "reviewed";

/** One article in one language, as the store's public API returns it (and the seed's front matter gives it). */
export interface HelpArticleFields {
  slug: string;
  lang: SeoLang;
  title: string;
  description: string;
  section: HelpSectionId;
  category: string;
  updated: string;
  keywords: string[];
  related: string[];
  faq: GuideFaq[];
  appPromo: "auto" | "off";
  translation: "original" | HelpTranslation;
  body: string;
  intercomId?: string;
  intercomUrl?: string;
}

/** What the public API's GET /help/content returns (and what a page embeds for the browser). */
export interface HelpSnapshot {
  catalogue: HelpCatalogue | null;
  /** slug → { lang: machine | reviewed } for every published article (hreflang and noindex need all of it). */
  translations: Record<string, Partial<Record<SeoLang, HelpTranslation>>>;
  articles: HelpArticleFields[];
  /** The languages `articles` covers. */
  langs: string[];
}

export interface HelpArticle {
  slug: string;
  lang: SeoLang;
  title: string;
  description: string;
  section: HelpSectionId;
  category: string;
  updated: string;
  keywords: string[];
  related: string[];
  intercomId?: string;
  intercomUrl?: string;
  appPromo: "auto" | "off";
  /** English is the original; a translation is "machine" until a native speaker has reviewed it. */
  translation: "original" | "machine" | "reviewed";
  faq: GuideFaq[];
  blocks: GuideBlock[];
  /** The H2s (for the search index and the "On this page" list). */
  headings: { id: string; text: string }[];
  /** All visible text, for search. */
  text: string;
  words: number;
  /** The <!-- … --> notes in the source (TODO(owner) items from the importer). */
  notes: string[];
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// --- The content in memory --------------------------------------------------------------------------------------
// Live bindings: applyHelpSnapshot() replaces them, and every importer sees the new values.

export let HELP_SECTIONS: HelpSection[] = SEED_CATALOGUE.sections as HelpSection[];
export let HELP_CATEGORIES: HelpCategory[] = sortCategories(SEED_CATALOGUE.categories as HelpCategory[]);
/** Every article in every language loaded so far (all of them on the server), sorted by title. */
export let ALL_HELP: HelpArticle[] = [];
export let HELP_SLUGS: string[] = [];
/** Every language any article is in ("en" first): the languages the hub, section and category pages are offered in. */
export let HELP_LANGS: SeoLang[] = ["en"];
let TRANSLATIONS: Record<string, Partial<Record<SeoLang, HelpTranslation>>> = {};
/** The fields each loaded article was built from, by "<slug>/<lang>" (what a page embeds for the browser). */
let FIELDS = new Map<string, HelpArticleFields>();
const loadedLangs = new Set<SeoLang>();
let version = 0;
const listeners = new Set<() => void>();

function sortCategories(c: HelpCategory[]): HelpCategory[] {
  return c.slice().sort((a, b) => a.order - b.order);
}

const one = (fm: FrontMatter, key: string, where: string, optional = false): string | undefined => {
  const v = fm[key];
  if (v === undefined || v === "") {
    if (optional) return undefined;
    throw new Error(`${where}: front matter "${key}" is missing`);
  }
  if (typeof v !== "string") throw new Error(`${where}: front matter "${key}" must be a single value`);
  return v;
};
const many = (fm: FrontMatter, key: string): string[] => {
  const v = fm[key];
  if (v === undefined) return [];
  return typeof v === "string" ? [v] : (v as unknown[]).map(String);
};

/** An article from its fields, checked against the categories. Throws on anything malformed. */
export function helpArticleFromFields(f: HelpArticleFields, where = `${f.slug}/${f.lang}`, categories: HelpCategory[] = HELP_CATEGORIES, sections: HelpSection[] = HELP_SECTIONS): HelpArticle {
  const lang = f.lang;
  if (!(SEO_LANGS as readonly string[]).includes(lang)) throw new Error(`${where}: unknown language ${lang}`);
  if (!f.slug || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(f.slug)) throw new Error(`${where}: bad slug "${f.slug}"`);
  if (!sections.some((s) => s.id === f.section)) throw new Error(`${where}: section must be ${sections.map((s) => s.id).join(" or ")}`);
  const cat = categories.find((c) => c.id === f.category);
  if (!cat) throw new Error(`${where}: no category "${f.category}"`);
  if (cat.section !== f.section) throw new Error(`${where}: category "${f.category}" belongs to section "${cat.section}"`);
  if (!ISO_DATE.test(f.updated)) throw new Error(`${where}: updated must be YYYY-MM-DD`);
  if (f.appPromo !== "auto" && f.appPromo !== "off") throw new Error(`${where}: appPromo is auto or off`);
  if (lang === "en" && f.translation !== "original") throw new Error(`${where}: "translation" is for translated files only`);
  if (lang !== "en" && f.translation !== "machine" && f.translation !== "reviewed") throw new Error(`${where}: translation is machine or reviewed`);
  if (!f.title || !f.description) throw new Error(`${where}: title and description are required`);
  if (!Array.isArray(f.faq) || f.faq.some((x) => !x || typeof x !== "object")) throw new Error(`${where}: faq must be a list of q/a pairs`);
  const fill = (s: string) => fillPlanTokens(s, lang);
  const faq = f.faq.map((x) => ({ q: fill(x.q), a: fill(x.a) }));
  const blocks = parseGuideBody(fill(f.body));
  const headings = blocks.flatMap((b) => (b.type === "heading" && b.level === 2 ? [{ id: b.id, text: b.text.replace(/\*\*|`/g, "") }] : []));
  const texts = [...blocksText(blocks), ...faq.flatMap((x) => [x.q, x.a])];
  return {
    slug: f.slug,
    lang,
    title: fill(f.title),
    description: fill(f.description),
    section: f.section,
    category: f.category,
    updated: f.updated,
    keywords: f.keywords ?? [],
    related: f.related ?? [],
    intercomId: f.intercomId,
    intercomUrl: f.intercomUrl,
    // The app card is a consumer-side thing only: never on a photographers' article.
    appPromo: f.section === "photos" ? f.appPromo : "off",
    translation: f.translation,
    faq,
    blocks,
    headings,
    text: texts.join(" "),
    words: countWords(texts),
    notes: [...f.body.matchAll(/<!--([\s\S]*?)-->/g)].map((x) => x[1].trim()),
  };
}

/** A seed file (path ends "<slug>/<lang>.md") → its fields. Throws on anything malformed. */
export function helpFieldsFromFile(path: string, src: string): HelpArticleFields {
  const m = /([\w-]+)\/([a-z]{2})\.md$/.exec(path);
  if (!m) throw new Error(`${path}: help articles live at src/content/help/articles/<slug>/<lang>.md`);
  const [, folder, lang] = m;
  if (!(SEO_LANGS as readonly string[]).includes(lang)) throw new Error(`${path}: unknown language ${lang}`);
  const { data, body } = splitFrontMatter(src);
  const where = path;
  const slug = one(data, "slug", where)!;
  if (slug !== folder) throw new Error(`${where}: slug "${slug}" doesn't match its folder "${folder}"`);
  const translation = one(data, "translation", where, lang === "en");
  if (lang === "en" && translation) throw new Error(`${where}: "translation" is for translated files only`);
  if (lang !== "en" && translation !== "machine" && translation !== "reviewed") throw new Error(`${where}: translation is machine or reviewed`);
  const faqRaw = data.faq ?? [];
  if (!Array.isArray(faqRaw) || faqRaw.some((x) => typeof x !== "object")) throw new Error(`${where}: faq must be a list of q/a pairs`);
  return {
    slug,
    lang: lang as SeoLang,
    title: one(data, "title", where)!,
    description: one(data, "description", where)!,
    section: one(data, "section", where)! as HelpSectionId,
    category: one(data, "category", where)!,
    updated: one(data, "updated", where)!,
    keywords: many(data, "keywords"),
    related: many(data, "related"),
    faq: faqRaw as GuideFaq[],
    appPromo: (one(data, "appPromo", where, true) ?? "auto") as "auto" | "off",
    translation: lang === "en" ? "original" : (translation as HelpTranslation),
    body,
    intercomId: one(data, "intercomId", where, true),
    intercomUrl: one(data, "intercomUrl", where, true),
  };
}

/** Builds an article from its seed file (path ends "<slug>/<lang>.md"). Throws on anything malformed. */
export function buildHelpArticle(path: string, src: string): HelpArticle {
  return helpArticleFromFields(helpFieldsFromFile(path, src), path);
}

// The SEED: every article file, bundled on the server and in tests only (never in the browser's bundle). It is the
// content until the API has answered, and the source of the one-time import into the store.
export const SEED_FILES = (
  import.meta.env.SSR || import.meta.env.MODE === "test"
    ? import.meta.glob("./articles/*/*.md", { query: "?raw", import: "default", eager: true })
    : {}
) as Record<string, string>;

/** The seed as a snapshot (all languages). */
export const seedSnapshot = (): HelpSnapshot => {
  const articles = Object.entries(SEED_FILES).map(([p, src]) => helpFieldsFromFile(p, src));
  const translations: HelpSnapshot["translations"] = {};
  for (const a of articles) {
    translations[a.slug] ??= {};
    if (a.lang !== "en") translations[a.slug][a.lang] = a.translation as HelpTranslation;
  }
  return { catalogue: SEED_CATALOGUE as HelpCatalogue, translations, articles, langs: [...SEO_LANGS] };
};

const notify = () => {
  version += 1;
  for (const fn of listeners) fn();
};

/**
 * Makes `snap` the help content. `merge` (the browser adding a language) keeps the other languages already loaded.
 * An article that doesn't build (a malformed body, an unknown category) is left out and reported, never thrown: one bad
 * article must not take /help down. Returns the problems.
 */
export function applyHelpSnapshot(snap: HelpSnapshot, { merge = false }: { merge?: boolean } = {}): string[] {
  const problems: string[] = [];
  const sections = (snap.catalogue?.sections?.length ? snap.catalogue.sections : HELP_SECTIONS) as HelpSection[];
  const categories = snap.catalogue?.categories?.length ? sortCategories(snap.catalogue.categories) : HELP_CATEGORIES;
  const fields = merge ? new Map(FIELDS) : new Map<string, HelpArticleFields>();
  const built = merge ? new Map(ALL_HELP.map((a) => [`${a.slug}/${a.lang}`, a])) : new Map<string, HelpArticle>();
  for (const f of snap.articles) {
    const key = `${f.slug}/${f.lang}`;
    try {
      built.set(key, helpArticleFromFields(f, key, categories, sections));
      fields.set(key, f);
    } catch (e) {
      problems.push(e instanceof Error ? e.message : String(e));
    }
  }
  // A translation whose English is missing can't be shown (every page falls back to English).
  const english = new Set([...built.values()].filter((a) => a.lang === "en").map((a) => a.slug));
  for (const [k, a] of built) {
    if (english.has(a.slug)) continue;
    built.delete(k);
    fields.delete(k);
  }
  HELP_SECTIONS = sections;
  HELP_CATEGORIES = categories;
  ALL_HELP = [...built.values()].sort((a, b) => a.title.localeCompare(b.title));
  FIELDS = fields;
  HELP_SLUGS = [...english].sort((a, b) => (getHelpArticle(a)?.title ?? a).localeCompare(getHelpArticle(b)?.title ?? b));
  TRANSLATIONS = Object.fromEntries(HELP_SLUGS.map((s) => [s, { ...(snap.translations?.[s] ?? {}) }]));
  // A language counts as loaded when the snapshot covers it (even with no article in it).
  if (!merge) loadedLangs.clear();
  for (const l of snap.langs ?? []) if ((SEO_LANGS as readonly string[]).includes(l)) loadedLangs.add(l as SeoLang);
  for (const a of ALL_HELP) loadedLangs.add(a.lang);
  HELP_LANGS = SEO_LANGS.filter((l) => l === "en" || HELP_SLUGS.some((s) => !!TRANSLATIONS[s]?.[l]));
  if (problems.length) console.warn(`[help] ${problems.length} article(s) left out:`, problems.slice(0, 5));
  notify();
  return problems;
}

/** The current content as a snapshot, limited to `langs` (all when empty): what a server-rendered page embeds. */
export const helpSnapshotFor = (langs: readonly string[] = []): HelpSnapshot => {
  const want = new Set(langs);
  return {
    catalogue: { sections: HELP_SECTIONS, categories: HELP_CATEGORIES },
    translations: TRANSLATIONS,
    articles: [...FIELDS.values()].filter((f) => !want.size || want.has(f.lang)),
    langs: want.size ? [...want] : [...loadedLangs],
  };
};

// The seed is the starting content wherever it's bundled (the server and tests); the browser starts empty and gets
// the page's own snapshot before it hydrates (src/entry-client.tsx).
if (Object.keys(SEED_FILES).length) applyHelpSnapshot(seedSnapshot());

/** Bumps whenever the content changes (for useSyncExternalStore). */
export const helpContentVersion = () => version;
export const subscribeHelpContent = (fn: () => void) => {
  listeners.add(fn);
  return () => void listeners.delete(fn);
};

/** Whether a language's articles are in memory (always, on the server; in the browser once loaded). */
export const helpLangReady = (lang: string): boolean =>
  loadedLangs.has(lang as SeoLang) || !(SEO_LANGS as readonly string[]).includes(lang) || !HELP_SLUGS.some((s) => !!TRANSLATIONS[s]?.[lang as SeoLang]);

const pendingLangs = new Map<string, Promise<void>>();
/** Loads a language's published articles from the public help API in the browser (a no-op where it's in memory). */
export async function loadHelpLang(lang: string, { force = false }: { force?: boolean } = {}): Promise<void> {
  if (!force && helpLangReady(lang)) return;
  if (!pendingLangs.has(lang)) {
    const p = (async () => {
      const { helpApiBase } = await import("./api");
      const res = await fetch(`${helpApiBase()}/help/content?langs=${encodeURIComponent(lang)}`);
      if (!res.ok) throw new Error(`help content ${res.status}`);
      const snap = (await res.json()) as HelpSnapshot;
      applyHelpSnapshot({ ...snap, catalogue: snap.catalogue ?? { sections: HELP_SECTIONS, categories: HELP_CATEGORIES }, langs: [lang] }, { merge: true });
    })().finally(() => pendingLangs.delete(lang));
    pendingLangs.set(lang, p);
  }
  return pendingLangs.get(lang);
}

/** The languages an article has ("en" first). */
export const helpLangs = (slug: string): SeoLang[] => {
  if (!HELP_SLUGS.includes(slug)) return [];
  const t = TRANSLATIONS[slug] ?? {};
  return SEO_LANGS.filter((l) => l === "en" || !!t[l]);
};

/** The languages an article is machine-translated into (served, but kept out of search until reviewed). */
export const helpMachineLangs = (slug: string): SeoLang[] => SEO_LANGS.filter((l) => TRANSLATIONS[slug]?.[l] === "machine");

/** The translation map as the loaded articles say it should be. */
export const translationsFromFiles = () =>
  Object.fromEntries(
    HELP_SLUGS.slice()
      .sort()
      .map((s) => [
        s,
        Object.fromEntries(
          ALL_HELP.filter((a) => a.slug === s && a.lang !== "en")
            .sort((a, b) => a.lang.localeCompare(b.lang))
            .map((a) => [a.lang, a.translation]),
        ),
      ]),
  );

/** An article in a language, falling back to English. */
export function getHelpArticle(slug: string, lang: SeoLang = "en"): HelpArticle | undefined {
  return ALL_HELP.find((a) => a.slug === slug && a.lang === lang) ?? ALL_HELP.find((a) => a.slug === slug && a.lang === "en");
}

/** One article per slug, in `lang` where it has it (else English). */
export const listHelp = (lang: SeoLang = "en"): HelpArticle[] =>
  HELP_SLUGS.map((s) => getHelpArticle(s, lang)).filter((a): a is HelpArticle => !!a);

export const getSection = (id: string) => HELP_SECTIONS.find((s) => s.id === id);
/** The sections the /help hub shows (every section but the separate ones). */
export const hubSections = () => HELP_SECTIONS.filter((s) => !isSeparateSection(s.id));
export const getCategory = (section: string, id: string) => HELP_CATEGORIES.find((c) => c.section === section && c.id === id);
export const sectionCategories = (section: string) => HELP_CATEGORIES.filter((c) => c.section === section);
export const categoryArticles = (category: string, lang: SeoLang = "en") => listHelp(lang).filter((a) => a.category === category);

/** A category's (or any per-language label's) text in a language, falling back to English. */
export const label = (m: { en: string } & Partial<Record<SeoLang, string>>, lang: SeoLang) => m[lang] ?? m.en;

export const helpArticlePath = (slug: string) => `${HELP_PATH}/articles/${slug}`;
export const helpSectionPath = (section: string) => `${HELP_PATH}/${section}`;
export const helpCategoryPath = (section: string, category: string) => `${HELP_PATH}/${section}/${category}`;

// --- Related articles -------------------------------------------------------------------------------------------
// 1. The ones the article names in its front matter, in that order.
// 2. Then the ones that name IT (named links are reciprocal: if A lists B and B has room, B shows A).
// 3. Then the closest of the rest by shared search keywords and title words (rarer words count for more), the same
//    section first, across categories; the same category only breaks ties. Two words in common at least (one in the
//    same category); otherwise not shown, except that a list shorter than 3 is topped up from the same category.

/** Too common to say two articles are related (on top of the search's stop words). */
const RELATED_STOP = new Set(["a", "an", "and", "are", "be", "by", "can", "do", "does", "for", "from", "get", "has", "have", "how", "i", "if", "in", "is", "it", "me", "my", "of", "on", "or", "the", "to", "what", "when", "where", "which", "who", "why", "with", "you", "your", "photohawk"]);

const relatedTerms = (texts: string[]): Set<string> =>
  new Set(texts.flatMap((x) => normalise(x).split(" ")).filter((w) => w && !RELATED_STOP.has(w) && (w.length >= 3 || /[^\p{Script=Latin}\p{N}]/u.test(w))));

interface RelatedProfile {
  keywords: Set<string>;
  title: Set<string>;
}

let relatedCache: { version: number; lang: SeoLang; profiles: Map<string, RelatedProfile>; idf: Map<string, number> } | null = null;

/** Each article's keyword and title words in `lang`, and how rare each word is across the help centre. */
const relatedIndex = (lang: SeoLang) => {
  if (relatedCache && relatedCache.version === version && relatedCache.lang === lang) return relatedCache;
  const articles = listHelp(lang);
  const profiles = new Map<string, RelatedProfile>();
  const df = new Map<string, number>();
  for (const x of articles) {
    const p = { keywords: relatedTerms(x.keywords), title: relatedTerms([x.title]) };
    profiles.set(x.slug, p);
    for (const w of new Set([...p.keywords, ...p.title])) df.set(w, (df.get(w) ?? 0) + 1);
  }
  const n = Math.max(articles.length, 1);
  const idf = new Map([...df].map(([w, d]) => [w, Math.log(1 + n / d)]));
  relatedCache = { version, lang, profiles, idf };
  return relatedCache;
};

/** The words two articles share (keywords and titles), and how related that makes them: rarer words count for more. */
const relatedMatch = (a: HelpArticle, b: HelpArticle): { score: number; shared: number } => {
  const { profiles, idf } = relatedIndex(a.lang);
  const pa = profiles.get(a.slug) ?? { keywords: relatedTerms(a.keywords), title: relatedTerms([a.title]) };
  const pb = profiles.get(b.slug) ?? { keywords: relatedTerms(b.keywords), title: relatedTerms([b.title]) };
  const termsA = new Set([...pa.keywords, ...pa.title]);
  let score = 0;
  let shared = 0;
  for (const w of new Set([...pb.keywords, ...pb.title])) {
    if (!termsA.has(w)) continue;
    shared += 1;
    // A keyword on both sides weighs most, then a title word on both sides; a keyword on one side and a title word on
    // the other counts a little.
    const both = (pa.keywords.has(w) && pb.keywords.has(w) ? 1.5 : 0) + (pa.title.has(w) && pb.title.has(w) ? 1 : 0);
    score += (both > 0 ? both : 0.75) * (idf.get(w) ?? 1);
  }
  return { score, shared };
};

/** How related two articles are by their keywords and titles: 0 when they share nothing. */
export const relatedScore = (a: HelpArticle, b: HelpArticle): number => relatedMatch(a, b).score;

/**
 * Close enough to suggest without being named: two or more words in common, or one in the same category. (One shared
 * word across categories is usually a coincidence, such as "bulk" in "bulk coupons" and "delete in bulk".)
 */
const closeEnough = (a: HelpArticle, b: HelpArticle): boolean => {
  const { shared } = relatedMatch(a, b);
  return shared >= 2 || (shared >= 1 && a.category === b.category);
};

/** The slugs whose (English, article-level) related list names `slug`. */
const namedBy = (slug: string): string[] => HELP_SLUGS.filter((s) => s !== slug && (getHelpArticle(s, "en")?.related ?? []).includes(slug));

/** Related articles, up to `max`: the named ones, then the ones that name this one, then the closest by keywords. */
export const relatedArticles = (a: HelpArticle, max = 6): HelpArticle[] => {
  const out: HelpArticle[] = [];
  const seen = new Set([a.slug]);
  const add = (x: HelpArticle | undefined) => {
    if (!x || seen.has(x.slug) || out.length >= max) return;
    seen.add(x.slug);
    out.push(x);
  };
  // A separate section's articles relate only to each other (SEPARATE_SECTIONS), whatever a list names.
  const wall = (x: HelpArticle | undefined) => (x && sameHelpWall(a.section, x.section) ? x : undefined);
  for (const s of a.related) add(wall(getHelpArticle(s, a.lang)));
  const rank = (xs: HelpArticle[]) =>
    xs
      .map((x) => ({ x, score: relatedScore(a, x) }))
      .sort(
        (p, q) =>
          Number(q.x.section === a.section) - Number(p.x.section === a.section) ||
          q.score - p.score ||
          Number(q.x.category === a.category) - Number(p.x.category === a.category) ||
          p.x.title.localeCompare(q.x.title),
      );
  for (const { x } of rank(namedBy(a.slug).map((s) => wall(getHelpArticle(s, a.lang))).filter((x): x is HelpArticle => !!x))) add(x);
  for (const { x } of rank(listHelp(a.lang).filter((x) => !seen.has(x.slug) && sameHelpWall(a.section, x.section)))) if (closeEnough(a, x)) add(x);
  // Words in common can be scarce (languages written without spaces): the same category keeps the list useful.
  if (out.length < 3) for (const x of listHelp(a.lang)) if (x.category === a.category && out.length < 3) add(x);
  return out;
};
